import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Pick, PickStatus, PickVisibility } from "@alpha/shared";
import { api, qs } from "../api";
import { Chip, Empty, ErrorBox, Loading, PageHeader } from "../components/ui";
import { formatDateTime } from "../env";

interface Page {
  items: Pick[];
  nextCursor: string | null;
}

export function PicksPage() {
  const navigate = useNavigate();
  const [visibility, setVisibility] = useState<PickVisibility | "">("");
  const [status, setStatus] = useState<PickStatus | "">("");
  const [mine, setMine] = useState(false);
  const [items, setItems] = useState<Pick[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<unknown>(null);
  const [more, setMore] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setState("loading");
    api
      .get<Page>(`/v1/picks${qs({ take: 25, visibility, status, mine: mine ? "true" : undefined })}`, ctrl.signal)
      .then((p) => {
        setItems(p.items);
        setCursor(p.nextCursor);
        setState("ready");
      })
      .catch((e: unknown) => {
        if (ctrl.signal.aborted) return;
        setError(e);
        setState("error");
      });
    return () => ctrl.abort();
  }, [visibility, status, mine, tick]);

  async function loadMore() {
    if (!cursor) return;
    setMore(true);
    try {
      const p = await api.get<Page>(`/v1/picks${qs({ take: 25, cursor, visibility, status, mine: mine ? "true" : undefined })}`);
      setItems((prev) => [...prev, ...p.items]);
      setCursor(p.nextCursor);
    } catch (e) {
      setError(e);
      setState("error");
    } finally {
      setMore(false);
    }
  }

  return (
    <section>
      <PageHeader title="Picks">
        <button type="button" className="btn primary" onClick={() => navigate("/picks/new")}>
          New pick
        </button>
      </PageHeader>
      <div className="card row filters-inline">
        <label>
          Visibility
          <select value={visibility} onChange={(e) => setVisibility(e.target.value as PickVisibility | "")}>
            <option value="">All</option>
            <option value="private">Private</option>
            <option value="internal">Internal</option>
            <option value="public">Public</option>
          </select>
        </label>
        <label>
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value as PickStatus | "")}>
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <label className="check">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
          Mine only
        </label>
      </div>

      <div className="card">
        {state === "loading" && <Loading />}
        {state === "error" && <ErrorBox error={error} onRetry={() => setTick((t) => t + 1)} />}
        {state === "ready" && items.length === 0 && <Empty>No picks match. Private picks are visible to their author only.</Empty>}
        {state === "ready" && items.length > 0 && (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Direction</th>
                  <th className="right">Entry</th>
                  <th className="right">Target</th>
                  <th className="right">Stop</th>
                  <th className="right">Risk %</th>
                  <th>Visibility</th>
                  <th>Status</th>
                  <th>Author</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <Link to={`/picks/${p.id}`}>{p.ticker}</Link>
                    </td>
                    <td>
                      <Chip value={p.direction} />
                    </td>
                    <td className="right num">{p.entry}</td>
                    <td className="right num">{p.target}</td>
                    <td className="right num">{p.stop}</td>
                    <td className="right num">{p.riskPerTradePct}</td>
                    <td>
                      <Chip value={p.visibility} />
                    </td>
                    <td>
                      <Chip value={p.status} />
                    </td>
                    <td className="muted">{p.authorName}</td>
                    <td className="num">{formatDateTime(p.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {cursor && (
              <div className="row center-row">
                <button type="button" className="btn small" disabled={more} onClick={() => void loadMore()}>
                  {more ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
